import React, { useState, useMemo } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAllMeasuresQuery,
  useSoftDeleteMeasureMutation,
  useRestoreMeasureMutation
} from "../../app/services/meseeknaApiV2"
import { FiSearch, FiCheckCircle, FiTrash2, FiRefreshCw, FiUsers, FiPlay } from "react-icons/fi"
import { MdOutlineInsights, MdOutlineCategory } from "react-icons/md"
import { HiOutlineOfficeBuilding } from "react-icons/hi"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckIcon = (props: { size?: number; className?: string }): JSX.Element => <FiCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const InsightsIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineInsights {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CategoryIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineCategory {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TrashIcon = (props: { size?: number; className?: string }): JSX.Element => <FiTrash2 {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const RestoreIcon = (props: { size?: number; className?: string }): JSX.Element => <FiRefreshCw {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UsersIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUsers {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const RunsIcon = (props: { size?: number; className?: string }): JSX.Element => <FiPlay {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />

const MeasuresPage: React.FC = () => {
  const { data, isLoading, refetch } = useGetAllMeasuresQuery({ includeInactive: true, withCounts: true })
  const [softDeleteMeasure] = useSoftDeleteMeasureMutation()
  const [restoreMeasure] = useRestoreMeasureMutation()

  const [searchQuery, setSearchQuery] = useState("")
  const [viewMode, setViewMode] = useState<"all" | "active" | "deleted">("active")
  const [actionMessage, setActionMessage] = useState<{ type: "success" | "error"; text: string } | null>(null)

  const measures = data?.data || []

  const filteredMeasures = useMemo(() => {
    let result = measures

    // Filter by view mode
    if (viewMode === "active") {
      result = result.filter(m => m.isActive)
    } else if (viewMode === "deleted") {
      result = result.filter(m => !m.isActive)
    }

    // Filter by search query
    if (searchQuery) {
      result = result.filter(m =>
        m.measureName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.measureCode && m.measureCode.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }

    return result
  }, [measures, viewMode, searchQuery])

  const activeMeasures = measures.filter(m => m.isActive)
  const deletedMeasures = measures.filter(m => !m.isActive)

  // Calculate totals
  const totalOrgs = measures.reduce((sum, m) => sum + (m.organizationCount || 0), 0)
  const totalUsers = measures.reduce((sum, m) => sum + (m.microlearningUsers || 0), 0)
  const totalRuns = measures.reduce((sum, m) => sum + (m.runCalculations || 0), 0)

  const handleDelete = async (measureId: number, measureName: string) => {
    try {
      await softDeleteMeasure({ measureId }).unwrap()
      setActionMessage({ type: "success", text: `"${measureName}" has been deleted` })
      refetch()
    } catch (error) {
      console.error("Error deleting measure:", error)
      setActionMessage({ type: "error", text: `Failed to delete "${measureName}"` })
    }
    setTimeout(() => setActionMessage(null), 5000)
  }

  const handleRestore = async (measureId: number, measureName: string) => {
    try {
      await restoreMeasure({ measureId }).unwrap()
      setActionMessage({ type: "success", text: `"${measureName}" has been restored` })
      refetch()
    } catch (error) {
      console.error("Error restoring measure:", error)
      setActionMessage({ type: "error", text: `Failed to restore "${measureName}"` })
    }
    setTimeout(() => setActionMessage(null), 5000)
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <InsightsIcon size={24} className="text-teal-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Measures Management
                  </h1>
                  <p className="text-gray-500">
                    View and manage assessment measures
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <InsightsIcon size={20} className="text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{measures.length}</p>
                    <p className="text-sm text-gray-500">Total</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckIcon size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{activeMeasures.length}</p>
                    <p className="text-sm text-gray-500">Active</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <TrashIcon size={20} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{deletedMeasures.length}</p>
                    <p className="text-sm text-gray-500">Deleted</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BuildingIcon size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalOrgs}</p>
                    <p className="text-sm text-gray-500">Org Links</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <UsersIcon size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
                    <p className="text-sm text-gray-500">ML Users</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <RunsIcon size={20} className="text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalRuns}</p>
                    <p className="text-sm text-gray-500">Runs</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Message */}
            {actionMessage && (
              <div className={`mb-6 p-4 rounded-lg border ${
                actionMessage.type === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}>
                <div className="flex items-center justify-between">
                  <p>{actionMessage.text}</p>
                  <button
                    onClick={() => setActionMessage(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search measures..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <CategoryIcon size={18} className="text-gray-400" />
                  <select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value as "all" | "active" | "deleted")}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="active">Active Measures</option>
                    <option value="deleted">Deleted Measures</option>
                    <option value="all">All Measures</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Measures Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
                  >
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="flex gap-4">
                      <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                      <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredMeasures.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <InsightsIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No measures found</h3>
                <p className="text-gray-500">
                  {searchQuery
                    ? "Try adjusting your search terms"
                    : viewMode === "deleted"
                      ? "No deleted measures"
                      : "No measures available"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMeasures.map((measure) => (
                  <div
                    key={measure.id}
                    className={`bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow ${
                      measure.isActive ? "border-gray-200" : "border-gray-300 bg-gray-50 opacity-75"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                        {measure.measureName}
                      </h3>
                      {measure.isActive ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                          <CheckIcon size={12} />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-600">
                          <TrashIcon size={12} />
                          Deleted
                        </span>
                      )}
                    </div>

                    {measure.measureCode && (
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm text-gray-600 px-2 py-0.5 bg-gray-100 rounded font-mono">
                          {measure.measureCode}
                        </span>
                      </div>
                    )}

                    {/* Usage Stats */}
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1" title="Organizations">
                        <BuildingIcon size={14} />
                        <span>{measure.organizationCount || 0}</span>
                      </div>
                      <div className="flex items-center gap-1" title="Microlearning Users">
                        <UsersIcon size={14} />
                        <span>{measure.microlearningUsers || 0}</span>
                      </div>
                      <div className="flex items-center gap-1" title="Run Calculations">
                        <RunsIcon size={14} />
                        <span>{measure.runCalculations || 0}</span>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      {measure.isActive ? (
                        <button
                          onClick={() => handleDelete(measure.id, measure.measureName)}
                          className="flex items-center gap-2 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
                        >
                          <TrashIcon size={14} />
                          Delete
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRestore(measure.id, measure.measureName)}
                          className="flex items-center gap-2 px-3 py-1.5 text-teal-600 hover:bg-teal-50 rounded-lg text-sm transition-colors"
                        >
                          <RestoreIcon size={14} />
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default MeasuresPage
